import {
  type DefaultError,
  queryOptions,
  skipToken,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { ms } from 'ms';

import { type UUID } from '~/types/common';

import { queryKeysCompany } from '../queryKeys';

import { fetchCompanyInvoiceReceivers } from './fetchCompanyInvoiceReceivers';
import { type CompanyInvoiceReceivers } from './types';

export function selectCompanyInvoiceReceivers(data: unknown): string[] {
  if (!data || typeof data !== 'object') return [];

  const invoiceReceivers = (data as { invoiceReceivers?: unknown })
    .invoiceReceivers;

  return Array.isArray(invoiceReceivers) &&
    invoiceReceivers.every(
      (email): email is string => typeof email === 'string',
    )
    ? invoiceReceivers
    : [];
}

type CompanyInvoiceReceiversQueryOptions<TData> = Omit<
  UseQueryOptions<CompanyInvoiceReceivers, DefaultError, TData>,
  'queryKey' | 'queryFn'
>;

/**
 * Generates query options for a company's invoice receiver email addresses.
 *
 * @param companyId - The ID of the company to fetch; the query is disabled while undefined.
 * @param options - Additional options for the query.
 * @returns Query options for the company's invoice receivers.
 */
// fallow-ignore-next-line unused-export -- query-options factory supports route loaders and prefetches
export const getCompanyInvoiceReceiversQueryOptions = <
  TData = CompanyInvoiceReceivers,
>({
  companyId,
  options,
}: {
  companyId: UUID | undefined;
  options?: CompanyInvoiceReceiversQueryOptions<TData>;
}) =>
  queryOptions({
    queryFn: companyId
      ? ({ signal }: QueryFunctionContext) =>
          fetchCompanyInvoiceReceivers(companyId, { signal })
      : skipToken,
    queryKey: queryKeysCompany.getInvoiceReceivers(companyId),
    staleTime: ms('30s'),
    ...options,
  });

/**
 * Gets invoice receiver email addresses configured for a company.
 *
 * @param companyId - The ID of the company to fetch.
 * @param options - Additional options for the query.
 * @returns Query result containing the company's invoice receivers.
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/get_company_invoice_receivers_company__id__invoice_receivers_get
 */
export const useQueryCompanyInvoiceReceivers = <
  TData = CompanyInvoiceReceivers,
>(
  companyId: UUID | undefined,
  options?: CompanyInvoiceReceiversQueryOptions<TData>,
) => useQuery(getCompanyInvoiceReceiversQueryOptions({ companyId, options }));
